import { useState } from 'react';
import { AlertTriangle, X, Loader2, Trash2 } from 'lucide-react';
import api from '../../services/api';

const CONFIRM_PHRASE = 'SUPPRIMER';

/**
 * Modale de confirmation pour la suppression définitive du compte.
 *
 * Workflow :
 *  1) Affichage des conséquences claires
 *  2) Saisie du mot de passe pour preuve d'identité
 *  3) Saisie d'une raison optionnelle (feedback de sortie)
 *  4) Saisie de la phrase de confirmation (anti-clic accidentel)
 *  5) Appel API delete + déconnexion + redirect
 */
export default function DeleteAccountModal({ onClose, onSuccess }) {
    const [password, setPassword] = useState('');
    const [reason, setReason] = useState('');
    const [confirmPhrase, setConfirmPhrase] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const isConfirmed = confirmPhrase.trim().toUpperCase() === CONFIRM_PHRASE && password.length > 0;

    const handleSubmit = async () => {
        if (!isConfirmed) return;
        setSubmitting(true);
        setError(null);
        try {
            await api.post('/user/delete-account.php', {
                action: 'confirm',
                password,
                reason: reason.trim(),
            });
            onSuccess?.();
        } catch (e) {
            setError(e.response?.data?.message || 'Erreur lors de la suppression');
            setSubmitting(false);
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.65)',
                zIndex: 9200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                overflow: 'auto',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: 'var(--card-bg, #1e293b)',
                    color: 'var(--text, #e2e8f0)',
                    border: '1px solid var(--border, #334155)',
                    borderRadius: 14,
                    padding: 24,
                    maxWidth: 540,
                    width: '100%',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
                    position: 'relative',
                }}
            >
                <button
                    onClick={onClose}
                    disabled={submitting}
                    style={{
                        position: 'absolute', top: 12, right: 12,
                        background: 'transparent', border: 'none',
                        color: 'var(--text-muted)', cursor: 'pointer', padding: 6,
                    }}
                    title="Fermer"
                >
                    <X size={18} />
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <AlertTriangle size={22} style={{ color: '#ef4444' }} />
                    <h2 style={{ margin: 0, fontSize: '1.15em' }}>Supprimer définitivement mon compte</h2>
                </div>

                <p style={{ margin: '0 0 16px', color: 'var(--text-muted)', fontSize: '0.9em' }}>
                    Cette action est <strong style={{ color: 'inherit' }}>irréversible</strong>.
                    Une fois confirmée, voici ce qu'il se passe :
                </p>

                <ul style={{
                    margin: '0 0 20px',
                    paddingLeft: 22,
                    fontSize: '0.88em',
                    lineHeight: 1.6,
                    color: 'var(--text-muted)',
                }}>
                    <li>Ton accès au site est <strong style={{ color: 'inherit' }}>immédiatement coupé</strong></li>
                    <li>Tes infos personnelles (prénom, email, mot de passe) sont <strong style={{ color: 'inherit' }}>effacées</strong></li>
                    <li>Tes thématiques personnelles et questions IA générées sont <strong style={{ color: 'inherit' }}>supprimées</strong></li>
                    <li>Tes abonnements en cours sont <strong style={{ color: 'inherit' }}>annulés</strong> sans remboursement</li>
                    <li>Tes statistiques de quiz sont conservées de manière <em>anonymisée</em> (aucun lien possible avec toi)</li>
                    <li>Tu reçois un email de confirmation à ton adresse actuelle</li>
                </ul>

                <p style={{ margin: '0 0 16px', fontSize: '0.88em', color: 'var(--text-muted)' }}>
                    Si tu changes d'avis, tu pourras toujours créer un nouveau compte plus tard (avec le même email
                    si tu veux).
                </p>

                {/* Mot de passe */}
                <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.88em', fontWeight: 600 }}>
                        Mot de passe actuel
                    </label>
                    <input
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Pour confirmer ton identité"
                        style={{
                            width: '100%', boxSizing: 'border-box',
                            padding: '9px 12px',
                            background: 'rgba(0,0,0,0.25)',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            color: 'inherit',
                            fontSize: '0.92em',
                        }}
                    />
                </div>

                {/* Raison */}
                <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.88em', fontWeight: 600 }}>
                        Pourquoi pars-tu ? <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.85em' }}>(optionnel — m'aide à améliorer le site)</span>
                    </label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Ex : j'ai trouvé une autre solution, je n'ai plus le temps, le contenu ne me convient pas..."
                        rows={2}
                        maxLength={500}
                        style={{
                            width: '100%', boxSizing: 'border-box',
                            padding: '9px 12px',
                            background: 'rgba(0,0,0,0.25)',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            color: 'inherit',
                            fontSize: '0.9em',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                        }}
                    />
                </div>

                {/* Confirmation phrase */}
                <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.88em', fontWeight: 600 }}>
                        Pour valider, tape <code style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', padding: '1px 6px', borderRadius: 3 }}>{CONFIRM_PHRASE}</code> ci-dessous
                    </label>
                    <input
                        type="text"
                        value={confirmPhrase}
                        onChange={(e) => setConfirmPhrase(e.target.value)}
                        placeholder={CONFIRM_PHRASE}
                        autoComplete="off"
                        style={{
                            width: '100%', boxSizing: 'border-box',
                            padding: '9px 12px',
                            background: 'rgba(0,0,0,0.25)',
                            border: `1px solid ${confirmPhrase.toUpperCase() === CONFIRM_PHRASE ? '#ef4444' : 'var(--border)'}`,
                            borderRadius: 6,
                            color: 'inherit',
                            fontSize: '0.95em',
                            letterSpacing: 1,
                            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                            textAlign: 'center',
                        }}
                    />
                </div>

                {error && (
                    <div style={{
                        padding: '10px 12px',
                        background: 'rgba(239,68,68,0.12)',
                        color: '#fca5a5',
                        border: '1px solid rgba(239,68,68,0.3)',
                        borderRadius: 6,
                        fontSize: '0.88em',
                        marginBottom: 12,
                    }}>
                        {error}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        style={{
                            background: 'transparent',
                            color: 'var(--text-muted)',
                            border: '1px solid var(--border)',
                            padding: '9px 18px',
                            borderRadius: 8,
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            fontSize: '0.92em',
                        }}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!isConfirmed || submitting}
                        style={{
                            background: !isConfirmed ? 'var(--border)' : 'linear-gradient(135deg, #dc2626, #b91c1c)',
                            color: 'white',
                            border: 'none',
                            padding: '9px 18px',
                            borderRadius: 8,
                            cursor: !isConfirmed ? 'not-allowed' : 'pointer',
                            fontSize: '0.92em',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            opacity: !isConfirmed ? 0.5 : 1,
                        }}
                    >
                        {submitting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                        {submitting ? 'Suppression…' : 'Supprimer définitivement'}
                    </button>
                </div>
            </div>
        </div>
    );
}
